type Callback = (value: string | null) => any;
type ListenConfig = Record<string, Callback>;

class BodyAttributesMutationObserver {
  private readonly callbacksMap: Record<string, Callback[]> = {};
  private readonly mutationObserver: MutationObserver;

  constructor() {
    this.mutationObserver = new MutationObserver(this.mutationCallback.bind(this));
  }

  mutationCallback(mutations: MutationRecord[]) {
    for (const mutation of mutations) {
      const mutatedAttribute = mutation.attributeName;

      if (mutation.type !== 'attributes' || !this.callbacksMap[mutatedAttribute]) break;

      const mutatedAttributeValue = (mutation.target as HTMLElement).getAttribute(mutatedAttribute);

      this.callbacksMap[mutatedAttribute].forEach((cb) => cb(mutatedAttributeValue));
    }
  }

  startObserve() {
    this.mutationObserver.observe(document.body, {
      attributes: true,
      attributeOldValue: false,
    });
  }

  endObserve() {
    this.mutationObserver.disconnect();
  }

  listen(config: ListenConfig) {
    if (!Object.keys(this.callbacksMap).length) {
      this.startObserve();
    }

    const unlistenCallbackMap = {};

    for (const attribute in config) {
      const cb = config[attribute];

      this.callbacksMap[attribute] ??= [];
      this.callbacksMap[attribute].push(cb);

      unlistenCallbackMap[attribute] = cb;
    }

    return () => {
      for (const attribute in unlistenCallbackMap) {
        this.callbacksMap[attribute] = this.callbacksMap[attribute].filter(
          (cb) => cb !== unlistenCallbackMap[attribute],
        );

        if (!this.callbacksMap[attribute].length) {
          delete this.callbacksMap[attribute];
        }
      }

      if (!Object.keys(this.callbacksMap).length) {
        this.endObserve();
      }
    };
  }
}

export const BodyAttrsObserver = new BodyAttributesMutationObserver();
